// src/pages/Register/RegisterPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaTimes,
  FaUser,
  FaUserPlus,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScollToTop";
import { registerUser, checkEmailExists } from "../../services/userServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import { getSettings } from "../../services/settingsServices";
import { toAbsolutePhotoUrl } from "../../services/settingsPhotoServices";

/* ================== Validation nom / prénom ================== */

const NAME_ALLOWED = /^[\p{L}\p{M}][\p{L}\p{M}'\- ]{0,48}$/u;
const VOWELS = "AEIOUYÀÂÄÆÉÈÊËÎÏÔÖÙÛÜŸaeiouyàâäæéèêëîïôöùûüÿ";

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub",
  logo_club: "",
  header_url1: "",
  header_url2: "",
  header1_photo_url: "",
  header2_photo_url: "",
  couleur_principale: "",
  primary_color: "",
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const looksLikeHumanName = (raw) => {
  const s = String(raw || "").trim();

  if (s.length < 2 || s.length > 50) {
    return { ok: false, reason: "2 à 50 caractères attendus." };
  }

  if (!NAME_ALLOWED.test(s)) {
    return {
      ok: false,
      reason: "Caractères autorisés : lettres, espace, - et '.",
    };
  }

  if (/[0-9_@#$%^&*~`+=/\\|<>{}[\]"]/u.test(s)) {
    return {
      ok: false,
      reason: "Pas de chiffres ni de symboles spéciaux.",
    };
  }

  const sepCount = (s.match(/[-']/g) || []).length;
  if (sepCount > 3) {
    return { ok: false, reason: "Trop de séparateurs (- ')." };
  }

  if (!Array.from(s).some((c) => VOWELS.includes(c))) {
    return { ok: false, reason: "Le nom doit contenir au moins une voyelle." };
  }

  if (/[bcdfghjklmnpqrstvwxyz]{10,}/i.test(s)) {
    return { ok: false, reason: "Suite de consonnes trop longue." };
  }

  if (/(.)\1\1\1/u.test(s)) {
    return { ok: false, reason: "Répétition de caractère anormale." };
  }

  let switches = 0;

  for (let i = 1; i < s.length; i += 1) {
    if (/[A-Za-z]/.test(s[i]) && /[A-Za-z]/.test(s[i - 1])) {
      const currentUpper = s[i] === s[i].toUpperCase();
      const previousUpper = s[i - 1] === s[i - 1].toUpperCase();

      if (currentUpper !== previousUpper) switches += 1;
    }
  }

  if (switches > 4) {
    return { ok: false, reason: "Casse irrégulière." };
  }

  const tokens = s.split(/[\s\-']+/).filter(Boolean);

  for (const token of tokens) {
    if (
      token.length > 2 &&
      !Array.from(token).some((character) => VOWELS.includes(character))
    ) {
      return {
        ok: false,
        reason: "Chaque partie du nom doit être plausible.",
      };
    }
  }

  return { ok: true };
};

const normalizePrenom = (value = "") => {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(/([\-'\s])/)
    .map((part) =>
      /^[-'\s]$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join("");
};

const InputError = ({ children }) => {
  if (!children) return null;

  return (
    <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-rose-600 dark:text-rose-300">
      <FaExclamationTriangle className="shrink-0 text-[11px]" />
      {children}
    </p>
  );
};

const ExistingEmailModal = ({ onClose, onResetPassword }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-[2rem] border border-white/10 bg-white p-6 shadow-2xl dark:bg-slate-950 sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>

        <div className="pr-10">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
            <FaEnvelope />
          </div>

          <h3 className="mt-5 font-barlow text-2xl font-bold text-slate-950 dark:text-white">
            Email déjà enregistré
          </h3>

          <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
            Cette adresse email est déjà associée à un compte. Vous pouvez
            réinitialiser votre mot de passe pour récupérer l’accès.
          </p>
        </div>

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={onResetPassword}
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            Réinitialiser
          </button>
        </div>
      </div>
    </div>
  );
};

function RegisterPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
        });

        const primaryColor =
          clubSettings.couleur_principale || clubSettings.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor,
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const logoUrl = useMemo(() => {
    const logo = toAbsolutePhotoUrl(settings.logo_club?.trim() || "");
    return logo || "/img/logos/logo_monappliclub.png";
  }, [settings.logo_club]);

  const backgroundUrl = useMemo(() => {
    const candidate =
      settings.header_url2 ||
      settings.header_url1 ||
      settings.header2_photo_url ||
      settings.header1_photo_url;

    return toAbsolutePhotoUrl(candidate?.trim?.() || "") || backgroundImage;
  }, [
    settings.header_url1,
    settings.header_url2,
    settings.header1_photo_url,
    settings.header2_photo_url,
  ]);

  const nomVal = useMemo(() => looksLikeHumanName(nom), [nom]);
  const prenomVal = useMemo(() => looksLikeHumanName(prenom), [prenom]);

  const emailOk = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }, [email]);

  const pwdOk = useMemo(() => password.length >= 10, [password]);

  const formOk =
    nomVal.ok &&
    prenomVal.ok &&
    emailOk &&
    pwdOk &&
    !checkingEmail &&
    !submitting;

  const passwordStrength = useMemo(() => {
    let score = 0;

    if (password.length >= 10) score += 1;
    if (/[A-ZÀ-Ý]/.test(password)) score += 1;
    if (/[a-zà-ÿ]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-zÀ-ÿ0-9]/.test(password)) score += 1;

    return score;
  }, [password]);

  const strengthLabel = useMemo(() => {
    if (!password) return "Minimum 10 caractères";
    if (passwordStrength <= 2) return "Sécurité faible";
    if (passwordStrength <= 4) return "Sécurité correcte";
    return "Sécurité forte";
  }, [password, passwordStrength]);

  const closeModal = () => setModalOpen(false);

  const handleResetPassword = () => {
    closeModal();
    navigate("/forgot-password");
  };

  const clearError = () => {
    if (errorMessage) setErrorMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!nomVal.ok) {
      setErrorMessage(`Nom invalide : ${nomVal.reason || ""}`.trim());
      return;
    }

    if (!prenomVal.ok) {
      setErrorMessage(`Prénom invalide : ${prenomVal.reason || ""}`.trim());
      return;
    }

    if (!emailOk) {
      setErrorMessage("Email invalide.");
      return;
    }

    if (!pwdOk) {
      setErrorMessage("Mot de passe : minimum 10 caractères.");
      return;
    }

    const cleanEmail = email.trim().toLowerCase();

    try {
      setCheckingEmail(true);

      const emailCheck = await checkEmailExists(cleanEmail);

      if (emailCheck?.error) {
        setErrorMessage(emailCheck.error);
        return;
      }

      if (emailCheck?.exists) {
        setModalOpen(true);
        return;
      }
    } catch (error) {
      console.error("Erreur vérification email :", error);
      setErrorMessage("Impossible de vérifier cette adresse email.");
      return;
    } finally {
      setCheckingEmail(false);
    }

    try {
      setSubmitting(true);

      const cleanNom = nom.trim();
      const cleanPrenom = normalizePrenom(prenom);

      const result = await registerUser(
        cleanNom,
        cleanPrenom,
        cleanEmail,
        password,
      );

      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      const token = localStorage.getItem("token");
      const message = `Un nouvel utilisateur a été créé : ${cleanNom} ${cleanPrenom} (${cleanEmail})`;

      try {
        await sendNotificationToAdmin(message, token);
      } catch (notificationError) {
        console.warn("Notification admin non envoyée :", notificationError);
      }

      navigate("/login");
    } catch (error) {
      console.error("Erreur inscription :", error);
      setErrorMessage(
        error?.response?.data?.message ||
          "Inscription impossible pour le moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${backgroundUrl}")`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/72 to-slate-950/60" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-120px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>

            <Link
              to="/"
              className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950 sm:inline-flex"
            >
              Accueil
            </Link>
          </div>

          <div className="grid overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl lg:grid-cols-[0.9fr_1.1fr]">
            <aside className="relative hidden overflow-hidden bg-slate-950/75 p-10 lg:block">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_36%)]" />

              <div className="relative flex h-full flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
                    <FaShieldAlt className="text-primary" />
                    Création de compte
                  </div>

                  <h1 className="mt-7 font-barlow text-5xl font-bold leading-tight text-white">
                    Rejoignez l’espace numérique du club.
                  </h1>

                  <p className="mt-5 max-w-md text-base leading-8 text-white/70">
                    Créez votre compte pour accéder aux services du club :
                    inscriptions, informations adhérents, messages et espace
                    personnel.
                  </p>
                </div>

                <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2 shadow-xl">
                      <img
                        src={logoUrl}
                        alt={settings.nom_club || "MonAppliClub"}
                        className="h-full w-full object-contain"
                        draggable="false"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                        Site officiel
                      </p>

                      <p className="mt-1 truncate font-barlow text-2xl font-bold text-white">
                        {settings.nom_club || "MonAppliClub"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <section className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative mx-auto max-w-xl">
                <div className="mb-8 text-center">
                  <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10">
                    <img
                      src={logoUrl}
                      alt={settings.nom_club || "MonAppliClub"}
                      className="h-full w-full object-contain"
                      draggable="false"
                      onError={(event) => {
                        event.currentTarget.src =
                          "/img/logos/logo_monappliclub.png";
                      }}
                    />
                  </div>

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <FaUserPlus />
                    Inscription
                  </div>

                  <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                    Créer un compte
                  </h2>

                  <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Renseignez vos informations pour accéder aux services du
                    club.
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="mt-1 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="nom"
                        className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                      >
                        Nom
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          id="nom"
                          type="text"
                          placeholder="Votre nom"
                          className={cn(
                            "h-12 w-full rounded-2xl border py-3 pl-11 pr-4 text-sm font-semibold outline-none transition",
                            "bg-white text-slate-900 placeholder:text-slate-400",
                            "focus:ring-4 focus:ring-primary/10",
                            "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                            nom.length && !nomVal.ok
                              ? "border-rose-300 focus:border-rose-500"
                              : "border-slate-200 focus:border-primary dark:border-white/10",
                          )}
                          value={nom}
                          onChange={(event) => {
                            setNom(event.target.value);
                            clearError();
                          }}
                          onBlur={() => setNom(nom.trim())}
                          required
                          autoComplete="family-name"
                        />
                      </div>

                      {nom.length > 0 && !nomVal.ok && (
                        <InputError>{nomVal.reason}</InputError>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="prenom"
                        className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                      >
                        Prénom
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          id="prenom"
                          type="text"
                          placeholder="Votre prénom"
                          className={cn(
                            "h-12 w-full rounded-2xl border py-3 pl-11 pr-4 text-sm font-semibold outline-none transition",
                            "bg-white text-slate-900 placeholder:text-slate-400",
                            "focus:ring-4 focus:ring-primary/10",
                            "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                            prenom.length && !prenomVal.ok
                              ? "border-rose-300 focus:border-rose-500"
                              : "border-slate-200 focus:border-primary dark:border-white/10",
                          )}
                          value={prenom}
                          onChange={(event) => {
                            setPrenom(event.target.value);
                            clearError();
                          }}
                          onBlur={() => setPrenom(normalizePrenom(prenom))}
                          required
                          autoComplete="given-name"
                        />
                      </div>

                      {prenom.length > 0 && !prenomVal.ok && (
                        <InputError>{prenomVal.reason}</InputError>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Email
                    </label>

                    <div className="relative">
                      <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="email"
                        type="email"
                        placeholder="votre@email.fr"
                        className={cn(
                          "h-12 w-full rounded-2xl border py-3 pl-11 pr-4 text-sm font-semibold outline-none transition",
                          "bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:ring-4 focus:ring-primary/10",
                          "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                          email.length && !emailOk
                            ? "border-rose-300 focus:border-rose-500"
                            : "border-slate-200 focus:border-primary dark:border-white/10",
                        )}
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          clearError();
                        }}
                        required
                        autoComplete="email"
                      />
                    </div>

                    {email.length > 0 && !emailOk && (
                      <InputError>Email invalide.</InputError>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Mot de passe
                    </label>

                    <div className="relative">
                      <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Choisissez un mot de passe"
                        className={cn(
                          "h-12 w-full rounded-2xl border py-3 pl-11 pr-14 text-sm font-semibold outline-none transition",
                          "bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:ring-4 focus:ring-primary/10",
                          "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                          password.length && !pwdOk
                            ? "border-rose-300 focus:border-rose-500"
                            : "border-slate-200 focus:border-primary dark:border-white/10",
                        )}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          clearError();
                        }}
                        required
                        autoComplete="new-password"
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary dark:hover:bg-white/10"
                        aria-label={
                          showPassword
                            ? "Masquer le mot de passe"
                            : "Afficher le mot de passe"
                        }
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between gap-3">
                        <p
                          className={cn(
                            "text-xs font-semibold",
                            pwdOk
                              ? "text-emerald-600 dark:text-emerald-300"
                              : "text-slate-500 dark:text-slate-400",
                          )}
                        >
                          {strengthLabel}
                        </p>

                        {(checkingEmail || submitting) && (
                          <p className="text-xs font-semibold text-primary">
                            {submitting
                              ? "Inscription..."
                              : "Vérification email..."}
                          </p>
                        )}
                      </div>

                      <div className="mt-2 grid grid-cols-5 gap-1.5">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <div
                            key={index}
                            className={cn(
                              "h-1.5 rounded-full transition",
                              passwordStrength > index
                                ? "bg-primary"
                                : "bg-slate-200 dark:bg-white/10",
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={!formOk}
                    className={cn(
                      "inline-flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-sm font-bold text-white shadow-lg transition",
                      "bg-primary hover:-translate-y-0.5 hover:brightness-110",
                      "focus:outline-none focus:ring-4 focus:ring-primary/20",
                      "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:brightness-100",
                    )}
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Inscription...
                      </>
                    ) : checkingEmail ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Vérification...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        S'inscrire
                      </>
                    )}
                  </button>

                  <div className="flex flex-col gap-3 pt-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 font-bold text-primary transition hover:underline"
                    >
                      <FaSignInAlt className="text-xs" />
                      Déjà un compte ?
                    </Link>

                    <Link
                      to="/forgot-password"
                      className="font-bold text-slate-600 transition hover:text-primary dark:text-slate-300"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                </form>

                <p className="mt-8 text-center text-xs leading-5 text-slate-400">
                  En créant un compte, vous pourrez accéder aux services
                  numériques proposés par le club.
                </p>
              </div>
            </section>
          </div>

          <p className="mt-5 text-center text-xs font-semibold text-white/70">
            MonAppliClub • Gestion de club simplifiée
          </p>
        </div>
      </div>

      {isModalOpen && (
        <ExistingEmailModal
          onClose={closeModal}
          onResetPassword={handleResetPassword}
        />
      )}
    </main>
  );
}

export default RegisterPage;
